import type { IconName } from "@/lib/icons";

/**
 * The work waiting on an admin, one queue per page where it gets done. A
 * count is `undefined` while it loads or when its request failed: the
 * dashboard then shows a dash, never a made-up 0.
 */
export type TodoKey = "payments" | "tests" | "translations" | "complaints" | "phraseFiles";

export type TodoCounts = Partial<Record<TodoKey, number | undefined>>;

export interface TodoKpi {
  key: TodoKey;
  label: string;
  hint: string;
  value: number | undefined;
  href: string;
  icon: IconName;
}

interface TodoCopyEntry {
  label: string;
  hint: string;
}

export type TodoCopy = Record<TodoKey, TodoCopyEntry>;

export const TODO_COPY: Record<"fr" | "en", TodoCopy> = {
  fr: {
    payments: { label: "Demandes de paiement", hint: "à approuver" },
    tests: { label: "Tests de qualification", hint: "à corriger" },
    translations: { label: "Traductions proposées", hint: "à valider" },
    complaints: { label: "Signalements", hint: "nouveaux ou en cours" },
    phraseFiles: { label: "Fichiers de phrases", hint: "à approuver" },
  },
  en: {
    payments: { label: "Payout requests", hint: "to approve" },
    tests: { label: "Qualification tests", hint: "to correct" },
    translations: { label: "Proposed translations", hint: "to review" },
    complaints: { label: "Reports", hint: "new or in progress" },
    phraseFiles: { label: "Phrase files", hint: "to approve" },
  },
};

/** Open reports: new ones plus the ones someone is already handling. */
export function openComplaints(stats: { byStatus?: Record<string, number> } | undefined): number | undefined {
  if (!stats) return undefined;
  return (stats.byStatus?.new ?? 0) + (stats.byStatus?.in_progress ?? 0);
}

/** Sum of per-language counts; unknown as soon as one of them is. */
export function sumCounts(values: Array<number | undefined>): number | undefined {
  let total = 0;
  for (const value of values) {
    if (value === undefined) return undefined;
    total += value;
  }
  return total;
}

/** Where each queue is worked on, in the admin space of that language. */
export function todoHref(key: TodoKey, lang: string): string {
  const base = `/${lang}/admin`;
  switch (key) {
    case "payments":
      return `${base}/payments`;
    case "tests":
      return `${base}/test-moderation`;
    case "translations":
      return `${base}/excel-translations`;
    case "complaints":
      return `${base}/complaints`;
    case "phraseFiles":
      return `${base}/uploads-excels?tab=phrases`;
  }
}

const ICONS: Record<TodoKey, IconName> = {
  payments: "wallet",
  tests: "checkCircle",
  translations: "languages",
  complaints: "flag",
  phraseFiles: "fileText",
};

/**
 * The cards of the dashboard's first row, in a fixed order. Approving phrase
 * files is a super admin action, so only a super admin gets that card.
 */
export function buildTodoKpis(counts: TodoCounts, lang: string, isSuperAdmin: boolean): TodoKpi[] {
  const copy = TODO_COPY[lang === "en" ? "en" : "fr"];
  const keys: TodoKey[] = ["payments", "tests", "translations", "complaints", ...(isSuperAdmin ? (["phraseFiles"] as const) : [])];
  return keys.map((key) => ({
    key,
    label: copy[key].label,
    hint: copy[key].hint,
    value: counts[key],
    href: todoHref(key, lang),
    icon: ICONS[key],
  }));
}

/** Menu badges: only known, non-zero counts. */
export function todoBadges(counts: TodoCounts): Partial<Record<TodoKey, number>> {
  const badges: Partial<Record<TodoKey, number>> = {};
  for (const [key, value] of Object.entries(counts) as Array<[TodoKey, number | undefined]>) {
    if (typeof value === "number" && value > 0) badges[key] = value;
  }
  return badges;
}
